import React from 'react';
import { Box, Typography, useTheme, Grid, List, ListItem, ListItemIcon } from '@mui/material';
import { FaClinicMedical, FaNotesMedical, FaAllergies, FaPills, FaHeartbeat } from 'react-icons/fa';
import {
  SectionProps,
  CarePlanFieldPath,
  HealthInfoGroup
} from '../../../../types/ndisSection/carePlanInfoTypes';
import { SectionHeader, ArrayField, CareTextField } from '../Components';
import { FiberManualRecord as BulletPointIcon } from '@mui/icons-material';

// Utility function to safely access nested properties
const getNestedValue = (obj: any, path: string): string => {
  if (!obj) return 'Not provided';
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    // Handle array indices (e.g., Groups[0])
    const match = key.match(/(\w+)\[(\d+)\]/);
    if (match) {
      const arrayKey = match[1]; // e.g., "Groups"
      const index = parseInt(match[2], 10); // e.g., 0
      if (
        current &&
        typeof current === 'object' &&
        arrayKey in current &&
        Array.isArray(current[arrayKey]) &&
        index < current[arrayKey].length
      ) {
        current = current[arrayKey][index];
      } else {
        return 'Not provided';
      }
    } else {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return 'Not provided';
      }
    }
  }
  return current ? String(current) : 'Not provided';
};

const HealthInfoSection: React.FC<SectionProps> = ({
  editMode,
  isMobile,
  handleEditToggle,
  handleSubmit,
  handleCancel,
  control,
  errors,
  getValues,
  carePlan,
  medicalHistory,
  setMedicalHistory,
  healthInfoGroups = [], // Default to empty array
  setHealthInfoGroups
}) => {
  const theme = useTheme();
  const spacing = isMobile ? 1 : 2;

  const groupFields: { label: string; name: keyof HealthInfoGroup; icon: JSX.Element }[] = [
    {
      label: 'Allergies',
      name: 'Allergies',
      icon: <FaAllergies />
    },
    {
      label: 'Current Medications',
      name: 'CurrentMedications',
      icon: <FaPills />
    },
    {
      label: 'Health Conditions',
      name: 'HealthConditions',
      icon: <FaHeartbeat />
    },
    {
      label: 'Primary Diagnosis',
      name: 'PrimaryDiagnosis',
      icon: <FaHeartbeat />
    },
    {
      label: 'Falls Risk',
      name: 'FallsRisk',
      icon: <FaHeartbeat />
    }
  ];

  // Ensure healthInfoGroups is in sync with carePlan
  React.useEffect(() => {
    if (carePlan?.HealthInformation?.Groups && carePlan.HealthInformation.Groups !== healthInfoGroups) {
      setHealthInfoGroups(carePlan.HealthInformation.Groups);
    }
  }, [carePlan, setHealthInfoGroups, healthInfoGroups]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        mx: 'auto'
      }}
    >
      <SectionHeader
        icon={<FaClinicMedical size={isMobile ? 16 : 20} />}
        title="Health Information"
        color={theme.palette.success.main}
        editMode={editMode}
        handleEditToggle={handleEditToggle}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        getValues={getValues}
      />

      <Box sx={{ p: isMobile ? 1 : 2 }}>
        {editMode ? (
          <>
            {/* Medical History Section */}
            <ArrayField
              name="HealthInformation.MedicalHistory"
              label="Medical History"
              icon={<FaNotesMedical size={isMobile ? 14 : 16} />}
              color={theme.palette.error.main}
              items={medicalHistory}
              setItems={setMedicalHistory}
              editMode={editMode}
              errors={errors}
            />
            {/* Health Details Section */}
            <Box sx={{ mt: spacing }}>
              <Typography
                variant={isMobile ? 'subtitle2' : 'h6'}
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 1
                }}
              >
                Health Details
              </Typography>
              {healthInfoGroups.map((group, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Group {index + 1}
                  </Typography>
                  <Grid container spacing={spacing}>
                    {groupFields.map((field) => (
                      <Grid item xs={12} sm={6} key={`${field.name}-${index}`}>
                        <CareTextField
                          label={field.label}
                          name={
                            `HealthInformation.Groups[${index}].${field.name}` as CarePlanFieldPath
                          }
                          control={control}
                          errors={errors}
                          getValues={getValues}
                          icon={React.cloneElement(field.icon, { size: isMobile ? 14 : 16 })}
                          size={isMobile ? 'small' : 'medium'}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setHealthInfoGroups([
                      ...healthInfoGroups,
                      {
                        Allergies: '',
                        CurrentMedications: '',
                        HealthConditions: '',
                        PrimaryDiagnosis: '',
                        FallsRisk: ''
                      }
                    ])
                  }
                  style={{
                    padding: '8px 16px',
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Add Health Details Group
                </button>
              </Box>
            </Box>
          </>
        ) : (
          <>
            {/* Medical History Section */}
            <Box
              sx={{
                p: isMobile ? 1 : 2,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                mb: spacing,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.error.contrastText,
                    borderRadius: '50%',
                    width: isMobile ? 32 : 36,
                    height: isMobile ? 32 : 36,
                    flexShrink: 0
                  }}
                >
                  <FaNotesMedical size={isMobile ? 14 : 16} />
                </Box>
                <Typography
                  variant={isMobile ? 'subtitle2' : 'h6'}
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary
                  }}
                >
                  Medical History To
                </Typography>
              </Box>

              {medicalHistory?.length > 0 ? (
                <List dense sx={{ py: 0 }}>
                  {medicalHistory.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <BulletPointIcon
                          sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}
                        />
                      </ListItemIcon>
                      <Typography variant="body2">{item}</Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 4 }}>
                  No medical history recorded
                </Typography>
              )}
            </Box>

            {/* Health Details Section */}
            <Box
              sx={{
                p: isMobile ? 1 : 2,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                mb: spacing,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.success.light,
                    color: theme.palette.success.contrastText,
                    borderRadius: '50%',
                    width: isMobile ? 32 : 36,
                    height: isMobile ? 32 : 36,
                    flexShrink: 0
                  }}
                >
                  <FaClinicMedical size={isMobile ? 14 : 16} />
                </Box>
                <Typography
                  variant={isMobile ? 'subtitle2' : 'h6'}
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary
                  }}
                >
                  Health Details
                </Typography>
              </Box>

              {healthInfoGroups?.length > 0 ? (
                healthInfoGroups.map((group, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography
                      variant={isMobile ? 'subtitle2' : 'subtitle1'}
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Health Details Group {index + 1}
                    </Typography>
                    <Grid container spacing={spacing}>
                      {groupFields.map((field) => {
                        const value = getNestedValue(
                          carePlan,
                          `HealthInformation.Groups[${index}].${field.name}`
                        );
                        return (
                          <Grid item xs={12} sm={6} key={`${field.name}-${index}`}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: isMobile ? 1 : 1.5,
                                backgroundColor: theme.palette.background.default,
                                borderRadius: 1,
                                minHeight: isMobile ? 56 : 64,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: theme.palette.success.light,
                                  color: theme.palette.success.contrastText,
                                  borderRadius: '50%',
                                  width: isMobile ? 32 : 36,
                                  height: isMobile ? 32 : 36,
                                  flexShrink: 0
                                }}
                              >
                                {React.cloneElement(field.icon, {
                                  size: isMobile ? 14 : 16
                                })}
                              </Box>
                              <Box sx={{ overflow: 'hidden', flex: 1 }}>
                                <Typography
                                  variant={isMobile ? 'caption' : 'subtitle2'}
                                  sx={{
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2
                                  }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  variant={isMobile ? 'body2' : 'body1'}
                                  sx={{
                                    fontWeight: 400,
                                    color: theme.palette.text.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }}
                                >
                                  {value}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 4 }}>
                  No health details recorded
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default HealthInfoSection;